import { useMemo } from 'react'
import { uniq, isEmpty, keys, reduce, sum, map, cloneDeep } from 'lodash'

import useSelectedNodes from 'flow/graph/useSelectedNodes'
import { useRelationTypes, useRelationCategories } from 'flow/settings/relations'

import edges from 'data/output/concept-deduped-links.json'

import arrayToDic from 'common/utils/array-to-dic'

const useFilteredRelations = () => {
  const [relationCategories, relationCategoriesAll] = useRelationCategories()
  const [relationsType, relationsTypeAll] = useRelationTypes()
  const selectedNodes = useSelectedNodes()

  const [selectedEdges, count] = useMemo(() => {
    // Count categories
    const categoriesCount = relationCategoriesAll.reduce((sum, item) => {
      sum[item] = 0
      return sum
    }, {})

    // Count Types
    const typesCount = relationsTypeAll.reduce((sum, item) => {
      sum[item] = 0
      return sum
    }, {})

    const dict = arrayToDic(selectedNodes)
    const selectedEdges = edges.reduce((p, d) => {
      if (dict[d.source] && dict[d.target]) {
        p.push(d)

        if (!isEmpty(d.values)) {
          relationCategoriesAll.forEach((category) => {
            if (d.values[category]) {
              const total = reduce(d.values[category], (sum, item) => {
                return sum + item
              }, 0)
              categoriesCount[category] += total
              relationsTypeAll.forEach((type) => {
                if (d.values[category][type]) {
                  typesCount[type] += d.values[category][type]
                }
              })
            }
          })
        }
      }
      return p
    }, [])

    return [
      selectedEdges,
      {
        categories: categoriesCount,
        types: typesCount
      }
    ]
  }, [selectedNodes, relationCategoriesAll, relationsTypeAll])

  const [filteredLinksWithUserSettings, filteredLinksWithUserSettingsValues, ontologyEdgeIds, competencyEdgeIds, nodeDegrees] = useMemo(() => {
    const nodeDegrees = {}
    const filteredIds = []
    const ontologyEdgeIds = {}
    const competencyEdgeIds = {}
    const filteredIdsValues = {}

    selectedEdges
      .forEach((c) => {
        if (c.values) {
          const newValues = cloneDeep(c.values)
          // Filter types
          relationCategoriesAll.forEach((category) => {
            if (!relationCategories.includes(category)) {
              if (newValues[category]) {
                delete newValues[category]
              }
            } else {
              // Filter Categories
              relationsTypeAll.forEach((type) => {
                if (!relationsType.includes(type)) {
                  if (newValues[category]) {
                    newValues[category][type] = 0
                  }
                }
              })
            }
          })

          if (keys(newValues).length) {
            const totalValues = reduce(newValues, (sumTotal, item) => {
              sumTotal += sum(map(item, (value) => value))
              return sumTotal
            }, 0)

            if (totalValues) {
              filteredIds.push(c.id)
              filteredIdsValues[c.id] = totalValues

              nodeDegrees[c.source] = nodeDegrees[c.source] + totalValues || totalValues
              nodeDegrees[c.target] = nodeDegrees[c.target] + totalValues || totalValues

              if (newValues.ontology) ontologyEdgeIds[c.id] = 1
              if (newValues.competency) competencyEdgeIds[c.id] = 1
            }
          }
        }
      })

    return [
      uniq(filteredIds),
      filteredIdsValues,
      ontologyEdgeIds,
      competencyEdgeIds,
      nodeDegrees
    ]
  }, [selectedEdges, relationCategories, relationCategoriesAll, relationsType, relationsTypeAll])

  return {
    selectedEdges,
    nodeDegrees,
    filteredLinksWithUserSettings,
    filteredLinksWithUserSettingsValues,
    ontologyEdgeIds,
    competencyEdgeIds,
    count
  }
}

export default useFilteredRelations
